import { useMemo, useState } from "react";

import { t } from "i18next";
import CollapsePanel from "antd/lib/collapse/CollapsePanel";
import { DownOutlined } from "@ant-design/icons";

import { Box } from "../../components/eventDashboard/Box";
import { Calendar } from "../../components/eventDashboard/Calendar";
import { CTA } from "../../components/eventDashboard/CTA";
import { Footer } from "../../components/eventDashboard/Footer";
import { Header } from "../../components/eventDashboard/Header";
import { Hero } from "../../components/eventDashboard/Hero";
import { LoginAndRegister } from "../../components/eventDashboard/LoginAndRegister";
import { Redundancy } from "../../components/eventDashboard/Redundancy";
import { Speakers } from "../../components/eventDashboard/Speakers";
import { Sponsors } from "../../components/eventDashboard/Sponsors";

import { MenuItemType, MenuPopover } from "../../components/shared-v2/MenuPopover";
import { ThemeButton } from "../../components/shared-v2/ThemeButton";
import { DragAndDropList } from "../../components/shared-v2/DragAndDropList";

import { EvtsFooter } from "../../components/shared/EvtsFooter";
import { HistoryIcon } from "../../components/shared/Icons/HistoryIcon";
import { ThreeDotsIcon } from "../../components/shared/Icons/ThreeDotsIcon";
import { TrashIcon } from "../../components/shared/Icons/TrashIcon";
import { ArrowRight } from "../../components/shared/Icons/ArrowRight";
import Button from "../../components/shared/button/Button";
import EventialsHeader from "../../components/shared/EventialsHeader";
import Modal from "../../components/shared/modal/Modal";
import Typography from "../../components/shared/typography/Typography";
import EyeIcon from "../../components/shared/Icons/EyeIcon";
import SixDotsIcon from "../../components/shared/Icons/SixDotsIcon";

import { IBox } from "../../interfaces/event/box";
import { ICalendar } from "../../interfaces/event/calendar";
import { IConfig } from "../../interfaces/event/config";
import { ICTA } from "../../interfaces/event/cta";
import { IFooter } from "../../interfaces/event/footer";
import { IHeader } from "../../interfaces/event/header";
import { IHero } from "../../interfaces/event/hero";
import { ILoginAndRegister } from "../../interfaces/event/loginAndRegister";
import { ISpeakers } from "../../interfaces/event/speakers";
import { ISponsors } from "../../interfaces/event/sponsors";

import {
  Container,
  Wrapper,
  NavMenu,
  Section,
  NavMenuItem,
  Divider,
  FooterNav,
  ModalContent,
  PreviewButton,
  HeaderNav,
  StyledCollapse
} from "./style";
import colors from "../../theme/colors";
import { breakpointSize } from "../../theme/mediaQuerie";

import { API_URL, WL_URL } from "../../settings";
import { reorder } from "../../utils/helper";
import { mockWL } from "./mock";

type TypeSections =
  'header' |
  'footer' |
  'redundancy' |
  'loginAndRegister' |
  'cta' |
  'box' |
  'hero' |
  'calendar' |
  'speakers' |
  'sponsors'

interface ISections {
  title: string;
  id: TypeSections;
}

const isMobile = window.innerWidth <= breakpointSize.Sm;

export const EventDashboard = () => {

  const [header, setHeader] = useState<IHeader>(mockWL.header);
  const [hero, setHero] = useState<IHero>(mockWL.body.hero);
  const [cta, setCta] = useState<ICTA>(mockWL.body.cta);
  const [box, setBox] = useState<IBox>(mockWL.body.box);
  const [speakers, setSpeakers] = useState<ISpeakers>(mockWL.body.speakers);
  const [calendar, setCalendar] = useState<ICalendar>(mockWL.body.calendar);
  const [sponsors, setSponsors] = useState<ISponsors>(mockWL.body.sponsors);
  const [loginAndRegister, setLoginAndRegister] = useState<ILoginAndRegister>(mockWL.body.loginAndRegister);
  const [footer, setFooter] = useState<IFooter>(mockWL.footer);
  const [config, setConfig] = useState<IConfig>(mockWL.config);

  const componentsSection = {
    header: <Header header={header} setHeader={setHeader} />,
    hero: <Hero hero={hero} setHero={setHero} />,
    cta: <CTA cta={cta} setCta={setCta} />,
    box: <Box box={box} setBox={setBox} />,
    calendar: <Calendar calendar={calendar} setCalendar={setCalendar} />,
    speakers: <Speakers speakers={speakers} setSpeakers={setSpeakers} />,
    sponsors: <Sponsors sponsors={sponsors} setSponsors={setSponsors} />,
    loginAndRegister: <LoginAndRegister loginAndRegister={loginAndRegister} setLoginAndRegister={setLoginAndRegister} />,
    footer: <Footer footer={footer} setFooter={setFooter} />,
    redundancy: <Redundancy config={config} setConfig={setConfig} />,
  }

  const [sections, setSections] = useState<ISections[]>([
    { title: t("dashboard.event.header.title"), id: 'header' },
    { title: t("dashboard.event.hero.title"), id: 'hero' },
    { title: t("dashboard.event.box.title"), id: 'box' },
    { title: t("dashboard.event.calendar.title"), id: 'calendar' },
    { title: t("dashboard.event.speakers.title"), id: 'speakers' },
    { title: t("dashboard.event.sponsors.title"), id: 'sponsors' },
    { title: t("dashboard.event.CTA.title"), id: 'cta' },
    { title: t("dashboard.event.loginAndRegister.title"), id: 'loginAndRegister' },
    { title: t("footer"), id: 'footer' },
    { title: t("redundancy"), id: 'redundancy' },
  ]);
  const [draggableSections, setDraggableSections] = useState<ISections[]>(sections);
  const [section, setSection] = useState<TypeSections>('header');

  const [modalTheme, setModalTheme] = useState(false);
  const [modalExclude, setModalExclude] = useState(false);
  const [modalPublish, setModalPublish] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);

  const menuItens: MenuItemType[] = useMemo(() => [
    {
      icon: <HistoryIcon color="label" />,
      title: t("history"),
      onClick: () => { }
    },
    {
      icon: <TrashIcon color="label" />,
      title: t("exclude"),
      onClick: () => { setModalExclude(true) }
    }
  ], []);

  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }
    setDraggableSections(previous => reorder(
      previous,
      result.source.index,
      result.destination.index
    ));
  }

  const onSaveReorder = () => {
    setHero(prev => ({ ...prev, index: draggableSections.findIndex(item => item.id === 'hero') }))
    setBox(prev => ({ ...prev, index: draggableSections.findIndex(item => item.id === 'box') }))
    setCalendar(prev => ({ ...prev, index: draggableSections.findIndex(item => item.id === 'calendar') }))
    setCta(prev => ({ ...prev, index: draggableSections.findIndex(item => item.id === 'cta') }))
    setSpeakers(prev => ({ ...prev, index: draggableSections.findIndex(item => item.id === 'speakers') }))
    setSponsors(prev => ({ ...prev, index: draggableSections.findIndex(item => item.id === 'sponsors') }))
    setSections(draggableSections);
    setReorderOpen(false);
  }

  return (
    <Container>

      {/*theme modal*/}
      <Modal
        show={modalTheme}
        handleShow={setModalTheme}
        title={`${config.theme === 'dark' ? 'Light' : 'Dark'} mode`}
        description={t("dashboard.event.theme.modalText")!}
      >
        <ModalContent>
          <div>
            <Button
              variant="cancel"
              fullWidth
              size="small"
              onClick={() => setModalTheme(false)}
              typographyType="headingXXS"
            >
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              fullWidth
              size="small"
              onClick={() => {
                setConfig(prev => ({ ...prev, theme: (prev.theme === 'dark' ? 'light' : 'dark') }))
                setModalTheme(false)
              }}
              typographyType="headingXXS"
            >
              {t("apply")}
            </Button>
          </div>
          <Typography type="paragraphS" color="title" >{t("dashboard.event.theme.modalFooter")}</Typography>
        </ModalContent>
      </Modal>

      {/*exclude modal*/}
      <Modal
        show={modalExclude}
        handleShow={setModalExclude}
        title={t("exclude")}
        description={t("dashboard.event.exclude.modalText")!}
      >
        <ModalContent>
          <div style={{ minWidth: '350px' }}>
            <Button
              variant="cancel"
              fullWidth
              size="small"
              onClick={() => setModalExclude(false)}
              typographyType="headingXXS"
            >
              {t("dashboard.event.exclude.cancel")}
            </Button>
            <Button
              size="small"
              variant="primary"
              fullWidth
              onClick={() => {
                setModalExclude(false)
              }}
              typographyType="headingXXS"
            >
              {t("dashboard.event.exclude.confirm")}
            </Button>
          </div>
          <Typography type="paragraphS" color="title" >{t("dashboard.event.exclude.modalFooter")}</Typography>
        </ModalContent>
      </Modal>

      {/*publish modal*/}
      <Modal
        show={modalPublish}
        handleShow={setModalPublish}
        title={t("dashboard.event.publish.modalTitle")}
        description={t("dashboard.event.publish.modalText")!}
      >
        <ModalContent>
          <div>
            <Button
              size="small"
              variant="cancel"
              fullWidth
              onClick={() => setModalPublish(false)}
              typographyType="headingXXS"
            >
              {t("dashboard.event.exclude.cancel")}
            </Button>
            <Button
              size="small"
              variant="primary"
              fullWidth
              onClick={() => setModalPublish(false)}
              typographyType="headingXXS"
            >
              {t("dashboard.event.exclude.confirm")}
            </Button>
          </div>
          <div style={{ flexDirection: 'column', gap: 0 }}>
            <Typography type="paragraphS" color="title" >{t("dashboard.event.publish.modalFooter")}</Typography>
            <a style={{ color: colors.blue }} href={WL_URL + "/whitelabelName"}>{API_URL + "/whitelabelName"}</a>
          </div>
        </ModalContent>
      </Modal>

      {/*reorder modal*/}
      <Modal
        show={!isMobile && reorderOpen}
        handleShow={setReorderOpen}
        title={t("dashboard.event.reorder.modalTitle")}
        description={t("dashboard.event.reorder.modalText")!}
      >
        <ModalContent>
          <DragAndDropList
            disabledDragItens={['header', 'footer', 'loginAndRegister']}
            handleDragEnd={handleDragEnd}
            list={draggableSections}
          />
          <div>
            <Button
              size="small"
              variant="cancel"
              onClick={() => {
                setReorderOpen(false);
                setDraggableSections(sections);
              }}
              typographyType="headingXXS"
            >
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              fullWidth
              size="small"
              onClick={onSaveReorder}
              typographyType="headingXXS"
            >
              {t("confirm")}
            </Button>
          </div>
        </ModalContent>
      </Modal>

      <EventialsHeader />

      <HeaderNav>
        <div>
          {!isMobile && (
            <>
              <Typography type="paragraphS" color="label">{t("manage")}</Typography>
              <ArrowRight color={colors.label} size={16} />
              <a onClick={() => window.location.href = (WL_URL + '/list-white-labels')}><Typography type="paragraphS" color="label">White Label</Typography></a>
              <ArrowRight color={colors.label} size={16} />
            </>
          )}
          <Typography type={isMobile ? "paragraphXXS" : "paragraphS"} color="title">{mockWL.name}</Typography>
        </div>
        <div>
          {isMobile ? (
            <>
              <ThemeButton
                colapsed
                themeType={config.theme}
                onClick={() => setModalTheme(true)}
              />
              <MenuPopover
                buttonIcon={<ThreeDotsIcon />}
                menuItens={[
                  {
                    title: t("reorder"),
                    icon: <SixDotsIcon color="label" />,
                    onClick: () => { setReorderOpen(true) }
                  },
                  ...menuItens
                ]}
                positionX={isMobile ? "left" : "right"}
                positionY={isMobile ? "bottom" : "top"}
              />
            </>
          ) : (
            <>
              <PreviewButton><EyeIcon size={'24px'} color="body" /></PreviewButton>
              <Button
                typographyType="paragraphS"
                variant="cancel"
                fullWidth
                onClick={() => setReorderOpen(true)}
              >
                {t("reorder")}
              </Button>
              <Button
                typographyType="headingXS"
                variant="primary"
                fullWidth
                onClick={() => setModalPublish(true)}
              >
                {t("publish")}
              </Button>
            </>
          )}
        </div>
      </HeaderNav>

      <Wrapper>
        <NavMenu>
          {
            isMobile ? (
              reorderOpen ? (
                <DragAndDropList
                  disabledDragItens={['header', 'footer', 'loginAndRegister']}
                  handleDragEnd={handleDragEnd}
                  list={draggableSections}
                />
              ) : (
                <StyledCollapse
                  accordion
                  expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} size={24} color={colors.title} />}
                  expandIconPosition="end"
                >
                  {
                    sections.map(({ title, id }) => (
                      <CollapsePanel
                        header={<Typography type="headingXXS">{title}</Typography>}
                        key={title}
                      >
                        <Section>{componentsSection[id]}</Section>
                      </CollapsePanel>
                    ))
                  }
                </StyledCollapse>
              )
            ) : (
              sections.map(({ title, id }, index) =>
                <NavMenuItem key={title} selected={id === section} onClick={() => setSection(id)}>
                  <Typography color={id === section ? "title" : "body"} type="headingXXS">{title}</Typography>
                </NavMenuItem>
              )
            )
          }
          <FooterNav>
            {!isMobile ? (
              <>
                <ThemeButton
                  themeType={config.theme}
                  onClick={() => {
                    setModalTheme(prev => !prev)
                  }}
                />
                <MenuPopover
                  buttonIcon={<ThreeDotsIcon />}
                  menuItens={menuItens}
                  positionX={isMobile ? "left" : "right"}
                  positionY={isMobile ? "bottom" : "top"}
                />
              </>
            ) : (
              <>
                <Button
                  typographyType="headingXXXS"
                  variant="cancel"
                  fullWidth
                  onClick={reorderOpen ? () => {
                    setReorderOpen(false);
                    setDraggableSections(sections);
                  } : () => { }}
                >
                  {reorderOpen ? t("cancel") : 'Preview'}
                </Button>
                <Button
                  typographyType="headingXXXS"
                  variant="primary"
                  fullWidth
                  onClick={reorderOpen ? onSaveReorder : () => setModalPublish(true)}
                >
                  {reorderOpen ? t("save") : t("publish")}
                </Button>
              </>
            )}
          </FooterNav>
        </NavMenu>
        <Divider />
        {!isMobile && (
          <Section>
            {componentsSection[section]}
          </Section>
        )}
      </Wrapper>
      <EvtsFooter />
    </Container>
  );
}